import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  FiHome, FiClipboard, FiUserPlus, FiHelpCircle,
  FiInfo,
} from 'react-icons/fi';
import Image from './atoms/Image';
import HamburgerMenu from './atoms/HamburgerMenu';
import Button from './atoms/Button';
import config from '../config/config';
import { RootState, logout, useLazyGetBusinessBySubdomainQuery } from '../store';
import { auth } from '../config/firebase-config';
import useGetDefaultBusinessId from '../hooks/useGetFirstBusinessId';
import useUpdateTitle from '../hooks/useUpdateTitle';
import getSubdomain from '../utils/getSubdomain';
import { BusinessDTO } from '../models/business';
import AvanttiIcon from './icons/AvanttiIcon';
import ThemeToggle from './ThemeToggle';
import Ticket from './icons/Ticket';
import AvanttiLogo from './icons/AvanttiLogo';
import { UserType } from '../models/user';

const MobileHeader: React.FC = () => {
  const [business, setBusiness] = useState<BusinessDTO | null>(null);
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { token } = useSelector((state: RootState) => state.auth);
  const businessId = useGetDefaultBusinessId();
  const dispatch = useDispatch();
  const [getBusinessBySubdomain, { isLoading: isLoadingBusiness }] = useLazyGetBusinessBySubdomainQuery();
  const userData = useSelector((state: RootState) => state.auth.user);
  const subdomain = getSubdomain();
  const expectBusinessLogo = !!subdomain;

  useUpdateTitle(business?.name);

  useEffect(() => {
    if (subdomain) {
      getBusinessBySubdomain(subdomain).then((res) => {
        if (res.data) {
          setBusiness(res.data);
        }
      });
    }
  }, [subdomain]);

  const handleMenuToggle = (open: boolean) => {
    setIsMenuOpen(open);
  };

  const handleClick = (path: string) => {
    const baseUrl = config.baseDomain.replace(/^https?:\/\//, '');
    if (subdomain) {
      window.location.href = `https://${subdomain}.${baseUrl}${path}`;
    } else {
      navigate(path);
    }
    setIsMenuOpen(false);
  };

  const handleClickLogin = () => {
    window.location.href = `${config.baseDomain}/login`;
  };

  const handleLogout = (path: string) => {
    handleClick(path);
    signOut(auth)
      .then(() => {
        dispatch(logout());
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const handleClickSellAvantti = () => {
    window.open('https://organizadores.avantti.io/', '_blank');
  };

  const handleClickHelp = () => {
    window.open('https://wa.me/message/GV2TPBX7VGT5A1', '_blank');
  };

  return (
    <>
      <header className="fixed z-50 w-full flex h-18 px-3.5 flex-col justify-center items-center bg-blur-lg bg-opacity-heavy border-b border-border-secondary self-stretch shadow-sm">
        <div className="flex py-4 pr-3 justify-between items-center self-stretch">
          <span
            onClick={() => {
              handleClick('/');
            }}
            className="flex h-18 cursor-pointer items-center"
          >
            {!expectBusinessLogo && <AvanttiIcon className="mr-3 text-text-primary" />}
            {expectBusinessLogo && business && <Image
              isDownloading={isLoadingBusiness}
              src={business.logo}
              className="mr-3 dark:invert"
              height={40}
              width={120}
            />}
          </span>
          <div className='w-fit ml-auto mr-4'>
            <ThemeToggle />
          </div>
          <HamburgerMenu isOpen={isMenuOpen} onToggle={handleMenuToggle} />
        </div>
      </header>
      {isMenuOpen && (
        <div
          ref={menuRef}
          style={{ top: '4.8rem' }}
          className={`fixed z-50 bg-blur-lg bg-opacity-heavy text-text-primary w-full transition-opacity duration-300 ease-in-out 
            ${isMenuOpen ? 'opacity-100 shadow-sm' : 'opacity-0'} top-18`}
        >
          <nav className="pb-2">
            <ul className="flex flex-col justify-center items-center">
              {token && userData.type === UserType.ROOT && (
                <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={() => handleClick('/admin')}>
                  <FiHome className='w-7 h-7 text-primary-500' />
                  <div className='text-md text-text-primary'>Panel de control</div>
                </li>
              )}
              {token && businessId && (
                <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={() => handleClick('/admin/all-events')}>
                  <Ticket className='w-7 h-7 text-primary-500' />
                  <div className='text-md text-text-primary'>Eventos</div>
                </li>
              )}
              {token && (
                <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={() => handleClick('/admin/businesses')}>
                  <FiClipboard className='w-7 h-7 text-primary-500' />
                  <div className='text-md text-text-primary'>Productoras</div>
                </li>
              )}
              {token && (
                <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={() => handleClick('/admin/invitations')}>
                  <FiUserPlus className='w-7 h-7 text-primary-500' />
                  <div className='text-md text-text-primary'>Invitaciones</div>
                </li>
              )}
              <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={() => handleClick('/')}>
                <AvanttiLogo className='w-7 h-7 text-primary-500' />
                <div className='text-md text-text-primary'>Plataforma Avantti</div>
              </li>
              {((business && business?.is_white_label === false) || !business) && <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={handleClickSellAvantti}>
                <FiInfo className='w-7 h-7 text-primary-500' />
                <div className='text-md text-text-primary'>Quiero vender con avantti</div>
              </li>}
              <li className="flex items-center p-4 h-12 cursor-pointer w-full gap-2 border-b border-border-secondary" onClick={handleClickHelp}>
                <FiHelpCircle className='w-7 h-7 text-primary-500' />
                <div className='text-md text-text-primary'>Centro de ayuda</div>
              </li>
              {!token && (
                <li className="flex items-center w-fit mx-auto mt-2">
                  <Button variant="secondary" onClick={handleClickLogin}>
                    Iniciar sesión
                  </Button>
                </li>
              )}
              {token && (
                <li className="flex items-center w-fit mx-auto mt-2">
                  <Button variant="secondary" onClick={() => handleLogout('/')}>
                    Cerrar sesión
                  </Button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}
      <div className='h-[76px]' />
    </>
  );
};

export default MobileHeader;
